import { useSearchParams } from "react-router-dom";
import styles from "./AccessDeniedPage.module.css";
import SoftindexLogo from "../../assets/images/softindex-website-logo.webp";
import { useAuth } from "../../context/authContext";

const REASON_MESSAGES = {
  unauthorized: "You don't have permission to access this page.",
  not_authenticated: "Please log in to access this page.",
  insufficient_permissions:
    "Your account doesn't have the required permissions for this action.",
  account_pending:
    "Your account is pending approval. Please contact support.",
  invitation_expired:
    "Your invitation has expired. Please request a new one.",
  user_not_found:
    "User account not found. Please check your credentials.",
  account_disabled:
    "Your account has been disabled. Please contact support to reactivate your account.",
};

const DEFAULT_MESSAGE = "Access to this page is restricted.";

export default function AccessDeniedPage() {
  const [searchParams] = useSearchParams();
  const { logout } = useAuth();

  const reason = searchParams.get("reason") || "unauthorized";

  const handleReturnToLogin = async () => {
    try {
      await logout();
    } finally {
      window.location.href = "/auth";
    }
  };

  const message = REASON_MESSAGES[reason] ?? DEFAULT_MESSAGE;

  return (
    <div className={styles.accessDeniedPage}>
      <div className={styles.container}>
        <div className={styles.logoSection}>
          <img
            src={SoftindexLogo}
            alt="Softindex Logo"
            className={styles.logo}
          />
        </div>

        <div className={styles.content}>
          <h1 className={styles.title}>Access Denied</h1>
          <p className={styles.subtitle}>{message}</p>

          <div className={styles.iconContainer}>
            <svg
              width="80"
              height="80"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className={styles.accessDeniedIcon}
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>

          <div className={styles.details}>
            <p className={styles.detailText}>
              If you believe this is an error, please contact our support team.
            </p>
          </div>

          <button
            type="button"
            onClick={handleReturnToLogin}
            className={styles.actionButton}
          >
            Return to Login
          </button>

          <div className={styles.helpSection}>
            <h3 className={styles.helpTitle}>Need Help?</h3>
            <div className={styles.helpContainer}>
              <div className={styles.helpOption}>
                <div className={styles.helpIcon}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <span className={styles.helpText}>
                  <a
                    href="mailto:support@softindex.ro"
                    className={styles.helpLink}
                  >
                    support@softindex.ro
                  </a>
                </span>
              </div>
              <div className={styles.helpOption}>
                <div className={styles.helpIcon}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <span className={styles.helpText}>
                  <a href="tel:+40757217748" className={styles.helpLink}>
                    +40 757 217 748
                  </a>
                </span>
              </div>
              <div className={styles.helpOption}>
                <div className={styles.helpIcon}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </div>
                <span className={styles.helpText}>
                  <a
                    href="https://www.softindex.ro/contact"
                    className={styles.helpLink}
                  >
                    www.softindex.ro/contact
                  </a>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
